/**
 * Los impuestos que el cajero enciende al cobrar: el IGTF, el IVA que se suma,
 * y los que cada negocio se haya creado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO ES UN ARCHIVO Y NO DOS BLOQUES DE JSX
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Se dibuja en dos sitios distintos y tiene que ser el mismo:
 *
 *   · En el PC, dentro del **resumen del pedido**, a la izquierda del cobro.
 *   · En el salón, dentro de la **ventana de cobrar la mesa**, donde no hay
 *     resumen al lado porque la cuenta la trae la comanda.
 *
 * Copiado dos veces, el día que se le cambie el texto a uno el otro se queda
 * con el viejo — y son dos pantallas del mismo local diciendo cosas distintas
 * sobre el mismo impuesto.
 *
 * ⚠️ AQUÍ NO SE PINTA NINGÚN MONTO, y es a propósito. Cuánto sale cada uno
 * depende de en qué moneda se pague, y eso todavía no se sabe: el número
 * aparece en el total del cobro en cuanto el cajero escribe con qué paga.
 * Poner una cifra antes de saberla sería inventarla.
 */

import { cx } from "@/components/ui";
import { formatMoney, type FormatoMoneda } from "@/lib/money";
import type { Tax } from "@/lib/types";

/** Lo exento de cada impuesto, ya escrito como dinero. Lo que da cero no sale. */
export function exentosComoDinero(
  exento: Record<string, number> | undefined,
  formato: FormatoMoneda,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(exento ?? {})
      .filter(([, monto]) => monto > 0)
      .map(([id, monto]) => [id, formatMoney(monto, formato)]),
  );
}

export function InterruptoresDeImpuesto({
  impuestos,
  encendidos,
  alAlternar,
  exentos = {},
  sobreQue = "Sobre el total del pedido",
  className,
}: {
  /** Los activos de alcance «pedido». Vacío = no se dibuja nada. */
  impuestos: Tax[];
  /** Identificadores de los que están puestos ahora mismo. */
  encendidos: string[];
  alAlternar: (id: string) => void;
  /**
   * Cuánto de este pedido está exento de cada impuesto, ya escrito como
   * dinero: `{id_del_iva: "$30,00"}`.
   *
   * ⚠️ Es lo único que se dice de montos aquí, y sí se puede decir: no depende
   * de cómo se pague, sino de qué se lleva el cliente. Sin esto, el cajero
   * enciende el IVA, ve un número que no es el 16 % del total, y cree que el
   * programa calculó mal.
   */
  exentos?: Record<string, string>;
  /** Lo que dice el que alcanza al total. En una entrada: «… de la factura». */
  sobreQue?: string;
  className?: string;
}) {
  if (impuestos.length === 0) return null;

  return (
    <div className={cx("space-y-1.5", className)}>
      {impuestos.map((t) => {
        const puesto = encendidos.includes(t.id);
        return (
          <button
            key={t.id}
            type="button"
            role="switch"
            aria-checked={puesto}
            onClick={() => alAlternar(t.id)}
            className={cx(
              "flex w-full items-center justify-between gap-3 rounded-[var(--radius-control)] border px-3 py-2 text-left transition-colors",
              puesto
                ? "border-accent bg-accent-soft"
                : "border-line bg-surface hover:border-line-strong",
            )}
          >
            <span className="min-w-0">
              <span
                className={cx(
                  "block truncate text-sm font-medium",
                  puesto ? "text-accent" : "text-ink",
                )}
              >
                {t.name} <span className="tabular">{t.rate_percent}%</span>
              </span>
              <span className="mt-0.5 block truncate text-xs text-muted">
                {t.currency_codes.length > 0
                  ? `Solo sobre lo que se pague en ${t.currency_codes.join(", ")}`
                  : sobreQue}
              </span>
              {exentos[t.id] && (
                <span className="mt-0.5 block truncate text-xs font-medium text-ink-soft">
                  Sin contar {exentos[t.id]} de productos exentos
                </span>
              )}
            </span>
            <span
              className={cx(
                "relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors",
                puesto ? "bg-accent" : "bg-line-strong",
              )}
            >
              <span
                className={cx(
                  "absolute top-0.5 size-4 rounded-full bg-surface transition-transform",
                  puesto ? "translate-x-[1.125rem]" : "translate-x-0.5",
                )}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}
